import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { usePaymentReview } from '@/features/payment-review/payment-review.api';

/**
 * The two views of booking management, as tabs.
 *
 * They are two routes, not two pieces of local state: each is worth linking to
 * and coming back to, and the browser's back button should move between them.
 * The tab strip just drives `navigate`, and the active tab is read from the URL
 * so a deep link opens on the right one.
 *
 * The review tab carries its open count, because that number is the reason to
 * switch to it. The query is shared with the review page itself, so showing it
 * here costs one request that the next click would have made anyway.
 */

export const BOOKINGS_PATH = '/booking/manage/bookings';
export const PAYMENT_REVIEW_PATH = '/booking/manage/payment-review';

export function BookingsTabs() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const review = usePaymentReview();
  const open = review.data?.open ?? 0;

  const value = pathname.startsWith(PAYMENT_REVIEW_PATH)
    ? PAYMENT_REVIEW_PATH
    : BOOKINGS_PATH;

  return (
    <Tabs value={value} onValueChange={(next) => navigate(next)}>
      <TabsList className="w-full sm:w-auto">
        <TabsTrigger value={BOOKINGS_PATH} className="sm:flex-none sm:px-4">
          {t('manageBookings.tabBookings')}
        </TabsTrigger>
        <TabsTrigger
          value={PAYMENT_REVIEW_PATH}
          className="sm:flex-none sm:px-4"
        >
          {t('manageBookings.tabPaymentReview')}
          {open > 0 && (
            <Badge variant="warning" className="ml-1 tabular-nums">
              {open}
            </Badge>
          )}
        </TabsTrigger>
      </TabsList>
    </Tabs>
  );
}
